<template>
	<div>
		<template v-if="waitingForUpload">
			<div v-if="smsEligible">
				<div>Wir haben dir eine SMS geschickt, bitte prüfe deine Nachrichten und öffne den darin enthaltenen
					Link.
				</div>
				<div>Dort findest du alle weiteren Informationen.</div>
			</div>
			<div v-if="qrEligible && qrCodeBase64" class="mt-2 text-center">
				<b-img :src="qrCodeBase64" fluid />
			</div>
			<div class="mt-2">
				<Loading text="Warte auf das Dokument"/>
			</div>
			<div class="mt-2">
				<a href="#" @click.prevent="abort">Legitimierung abbrechen</a>
			</div>
		</template>
		<template v-else>
			<FormGroup
				label="Bitte wähle aus, welches Dokument du hochladen willst: "
				:state="documentTypeValid"
			>
				<RadioGroup
					v-model="documentType"
					:options="documentTypeOptions"
					stacked
					@input="emitChangeEvent"
				/>
			</FormGroup>
			<FormGroup
				v-if="documentType"
				label="Wie willst du deinen Ausweis hochladen zur Legitimation?"
				:state="typeValid"
				class="mt-3"
			>
				<RadioGroup
					v-model="type"
					:options="typeOptions"
					stacked
					@input="emitChangeEvent"
				/>
			</FormGroup>
			<div
				v-if="type"
				class="mt-2"
			>
				<template v-if="localEligible">
					<FormGroup :state="localFileValid">
						<b-row>
							<b-col md="10">
								<FileInput
									v-model="localFile"
									@input="emitChangeEvent"
								/>
								<div v-if="localFile" class="mt-3">
									<div class="color-red">Legitimationsbestätigung</div>
									<FormGroup
										label="Die hinaufgeladene Datei enthält einen amtlichen Lichtbildausweis von mir persönlich mit einem einem Foto von meinem eigenen Gesicht:"
										:state="face !== false"
										invalid-feedback="Bitte verwende ein persönliches Ausweisdokument von dir mit einem Foto von deinem Gesicht."
										feedback-beneath
										class="mt-3"
									>
										<RadioGroup
											v-model="face"
											:options="faceOptions"
											@input="emitChangeEvent"
										/>
									</FormGroup>
									<FormGroup
										label="Der Text des Ausweises ist gut leserlich und auf dem Foto ist mein Gesicht klar sichtbar:"
										:state="clearness !== false"
										invalid-feedback="Bitte lade ein gut leserliches Ausweisdokument hoch"
										feedback-beneath
										class="mt-3"
									>
										<RadioGroup
											v-model="clearness"
											:options="clearnessOptions"
											@input="emitChangeEvent"
										/>
									</FormGroup>
								</div>
							</b-col>
							<b-col md="2">
								<div v-if="localFile">
									<b-img :src="localFile.base64" fluid/>
								</div>
							</b-col>
						</b-row>
					</FormGroup>
				</template>
				<template v-if="smsEligible">
					<FormGroup label="Mobilnummer" :state="phoneValid">
						<Phone
							v-model="phone"
							:meta="meta"
							@input="emitChangeEvent"
						/>
					</FormGroup>
					<div class="mt-2">
						<Button
							text="SMS senden"
							:disabled="!phoneValid || requesting"
							@click="requestLink"
						/>
					</div>
				</template>
				<template v-if="emailEligible">
					<FormGroup
						label="E-Mail"
						:state="emailValid"
						:invalid-feedback="emailError"
						feedback-beneath
					>
						<b-input
							v-model="email"
							@input="emitChangeEvent"
						/>
					</FormGroup>
					<div class="mt-2">
						<Button
							text="E-Mail senden"
							:disabled="!emailValid || requesting"
							@click="requestLink"
						/>
					</div>
				</template>
				<template v-if="qrEligible">
					<Button
						text="QR-Code erstellen"
						:disabled="requesting"
						@click="requestLink"
					/>
				</template>
			</div>
		</template>
	</div>
</template>

<script>
import Button from "@/vue/common/Button";
import FormGroup from "@/vue/common/form/Group";
import Phone from "@/vue/common/form/Phone";
import Select from "@/vue/common/form/Select";
import {isDefined} from "@/helper/Value";
import FileInput from "./FileInput"
import Loading from "@/vue/common/Loading";
import Add from "@/api/identification/Add";
import Get from "@/api/identification/Get";
import {FINISHED} from "@/helper/legitimation/identification/Status";
import {isEmptyOrNull} from "@/helper/String";
import * as EmailValidator from "email-validator";
import RadioGroup from "@/vue/common/form/RadioGroup";

const TYPE_LOCAL = 'local';
const TYPE_SMS = 'sms';
const TYPE_EMAIL = 'email';
const TYPE_QR = 'qr';

export default {

	props: {
		meta: {
			type: Object,
			required: true,
		},
		preFillPhone: {
			type: Object,
			required: false,
		},
		preFillEmail: {
			type: String,
			required: false,
		},
	},

	components: {
		Loading,
		RadioGroup,
		Button,
		FormGroup,
		Phone,
		Select,
		FileInput,
	},

	data ()
	{
		return {
			documentType: undefined,
			type: undefined,
			documentTypeOptions: [
				{ value: 'passport', text: 'Reisepass' },
				{ value: 'id-card', text: 'Personalausweis' },
				{ value: 'driving-license', text: 'Führerschein' },
			],
			typeOptions: [
				{
					value: TYPE_LOCAL,
					text: 'Von diesem Gerät auswählen'
				},
				{
					value: TYPE_SMS,
					text: 'Von einem anderen Gerät hochladen (ich will einen Link per SMS erhalten)'
				},
				{
					value: TYPE_EMAIL,
					text: 'Von einem anderen Gerät hochladen (ich will einen Link per E-Mail erhalten)'
				},
				{
					value: TYPE_QR,
					text: 'Von einem anderen Gerät hochladen (ich will einen QR-Code scannen)'
				},
			],
			requesting: false,
			localFile: null,
			phone: this.preFillPhone || null,
			email: this.preFillEmail || '',
			waitingForUpload: false,
			identificationId: null,
			qrCodeBase64: null,
			face: undefined,
			faceOptions: [
				{ value: true, text: 'Ja' },
				{ value: false, text: 'Nein' },
			],
			clearness: undefined,
			clearnessOptions: [
				{ value: true, text: 'Ja' },
				{ value: false, text: 'Nein' },
			],
			finished: false,
		};
	},

	computed: {
		documentTypeValid ()
		{
			return isDefined(this.type);
		},
		typeValid ()
		{
			return isDefined(this.type);
		},
		localEligible ()
		{
			return this.type === TYPE_LOCAL;
		},
		localFileValid ()
		{
			return !this.localEligible || isDefined(this.localFile);
		},
		smsEligible ()
		{
			return this.type === TYPE_SMS;
		},
		phoneValid ()
		{
			return !this.smsEligible || isDefined(this.phone);
		},
		emailEligible ()
		{
			return this.type === TYPE_EMAIL;
		},
		emailValid ()
		{
			return !this.emailEligible || !this.emailError;
		},
		emailError ()
		{
			if (!isEmptyOrNull(this.email) && !EmailValidator.validate(this.email))
			{
				return 'Ungültiges Format';
			}

			return null;
		},
		qrEligible ()
		{
			return this.type === TYPE_QR;
		},
		faceValid ()
		{
			return !this.localFile || this.face === true;
		},
		clearnessValid ()
		{
			return !this.localFile || this.clearness === true;
		},
		dataValid ()
		{
			return this.documentTypeValid
				&& this.typeValid
				&& this.localFileValid
				&& this.phoneValid
				&& this.emailValid
				&& this.faceValid
				&& this.clearnessValid;
		}
	},

	methods: {
		async requestLink ()
		{
			this.requesting = true;
			// TODO abort after some time (5 minutes or so)?
			try
			{
				const add = new Add()
					.setDocumentType(this.documentType)
					.setType(this.type);

				if (this.smsEligible)
				{
					add.setPhone(this.phone);
				}

				if (this.emailEligible)
				{
					add.setEmail(this.email);
				}

				const response = await add.call();

				this.identificationId = response.data.identification.id;
				this.qrCodeBase64 = response.data.qrCodeBase64;

				this.waitInterval = setInterval(
					() => this.check(),
					5000
				);

				this.waitingForUpload = true;
			}
			catch (e)
			{

			}

			this.requesting = false;
		},
		async check ()
		{
			const response = await new Get()
				.setIdentificationId(this.identificationId)
				.call();

			if (response.data.status === FINISHED)
			{
				clearInterval(this.waitInterval);

				this.waitingForUpload = false;
				this.finished = true;

				this.emitChangeEvent();
			}
		},
		abort ()
		{
			if (this.waitInterval)
			{
				clearInterval(this.waitInterval);
			}

			this.waitingForUpload = false;
		},
		emitChangeEvent ()
		{
			if (!this.dataValid)
			{
				this.$emit('input', undefined);
				return;
			}

			if (this.localFile)
			{
				this.$emit('input', {
					documentType: this.documentType,
					file: this.localFile,
					async: false,
				});

				return;
			}

			if (this.finished)
			{
				this.$emit('input', {
					identificationId: this.identificationId,
					async: true
				});

				return;
			}

			this.$emit('input', undefined);
		}
	}
}
</script>
